import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { House, Lock, EnvelopeSimple } from "@phosphor-icons/react";
import api, { formatApiError, setTokens } from "@/lib/api";
import { getCurrentPortal, PORTALS, landingForUser, urlForPortal } from "@/lib/portal";

export default function Login() {
  const { login, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setup, setSetup] = useState({
    company_name: "", first_name: "", last_name: "", email: "", password: "", position: "Geschäftsführer",
  });

  const portal = getCurrentPortal();
  const portalCfg = PORTALS[portal] || PORTALS.app;

  useEffect(() => {
    api.get("/auth/setup-needed").then((r) => {
      const needed = !!(r.data && r.data.needed);
      setSetupNeeded(needed);
      setSetupOpen(needed); // auto-open setup screen on empty DB
    }).catch(() => {});
  }, []);

  // Redirect already-authenticated users to their landing page.
  useEffect(() => {
    if (user && user !== false) {
      navigate(landingForUser(user), { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      const { access_token, refresh_token } = r.data || {};
      if (access_token) setTokens(access_token, refresh_token);
      await refresh?.();
      navigate("/", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setErr('Zu viele Fehlversuche. Bitte warte ca. 15 Minuten und versuche es erneut, oder nutze „Passwort vergessen?".');
      } else {
        setErr("Anmeldung fehlgeschlagen. Bitte E-Mail/Passwort prüfen.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onSetup = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const r = await api.post("/auth/setup-initial", setup);
      const { access_token, refresh_token } = r.data || {};
      if (access_token) setTokens(access_token, refresh_token);
      await refresh?.();
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setErr(null);
    setForgotMsg(null);
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotMsg("Falls ein Konto existiert, wurde eine E-Mail gesendet.");
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.svg" alt="Domivus" className="w-11 h-11" />
          <span className="text-2xl font-semibold tracking-tight">Domivus</span>
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight leading-tight mb-4">
            Hausverwaltung neu gedacht.<br />
            <span className="text-slate-300 text-2xl font-normal">Digital. Strukturiert. Sicher.</span>
          </h1>
          <p className="text-slate-300 text-sm max-w-md leading-relaxed">
            Objekte, Mietverhältnisse, Mietkonten, Dokumente, Aufgaben &mdash; alles
            an einem Ort. Speziell entwickelt für moderne Hausverwaltungen.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            <Stat icon={<House size={20} />} label="Objektakten" />
            <Stat icon={<EnvelopeSimple size={20} />} label="Schriftverkehr" />
            <Stat icon={<Lock size={20} />} label="DSGVO-konform" />
          </div>
        </div>
        <div className="text-xs text-slate-500">© 2026 Domivus &middot; Hausverwaltungssoftware</div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6 sm:mb-8">
            <img src="/logo.svg" alt="Domivus" className="w-10 h-10" />
            <div>
              <div className="text-xl font-semibold tracking-tight leading-none">Domivus</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Hausverwaltung</div>
            </div>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-1">
            {setupOpen ? "Erst-Einrichtung" : (forgotMode ? "Passwort zurücksetzen" : "Anmelden")}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {setupOpen
              ? "Diese Domivus-Instanz ist noch leer. Legen Sie hier Ihr Unternehmen und den ersten Administrator an."
              : (forgotMode ? "Geben Sie Ihre E-Mail-Adresse ein." : "Willkommen bei Domivus. Melden Sie sich mit Ihren Zugangsdaten an – Sie werden automatisch zum passenden Bereich weitergeleitet.")}
          </p>

          {setupOpen ? (
            <form onSubmit={onSetup} className="space-y-3" data-testid="setup-form">
              <div>
                <Label className="text-slate-700">Firmenname (Mandant)</Label>
                <Input required value={setup.company_name} onChange={(e) => setSetup({ ...setup, company_name: e.target.value })}
                  placeholder="z. B. Hausverwaltung Ruschmeier" className="mt-1.5 h-11" data-testid="setup-company" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Vorname</Label>
                  <Input required value={setup.first_name} onChange={(e) => setSetup({ ...setup, first_name: e.target.value })}
                    className="mt-1.5 h-11" data-testid="setup-first-name" />
                </div>
                <div>
                  <Label className="text-slate-700">Nachname</Label>
                  <Input required value={setup.last_name} onChange={(e) => setSetup({ ...setup, last_name: e.target.value })}
                    className="mt-1.5 h-11" data-testid="setup-last-name" />
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Position / Funktion</Label>
                <Input value={setup.position} onChange={(e) => setSetup({ ...setup, position: e.target.value })}
                  placeholder="Geschäftsführer / Inhaber / Hausverwalter" className="mt-1.5 h-11" data-testid="setup-position" />
              </div>
              <div>
                <Label className="text-slate-700">E-Mail (Login)</Label>
                <Input type="email" required value={setup.email} onChange={(e) => setSetup({ ...setup, email: e.target.value })}
                  className="mt-1.5 h-11" data-testid="setup-email" />
              </div>
              <div>
                <Label className="text-slate-700">Passwort (min. 8 Zeichen)</Label>
                <Input type="password" required minLength={8} value={setup.password} onChange={(e) => setSetup({ ...setup, password: e.target.value })}
                  className="mt-1.5 h-11" data-testid="setup-password" />
              </div>
              {err && (
                <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded-sm" data-testid="setup-error">{err}</div>
              )}
              <Button type="submit" disabled={loading}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-11 text-base mt-2"
                data-testid="setup-submit">
                {loading ? "…" : "Domivus einrichten"}
              </Button>
              <p className="text-[11px] text-slate-500 italic mt-2">
                Mit der Erst-Einrichtung legen Sie sich als <strong>Super-Administrator</strong> an.
                Anschließend können Sie weitere Benutzer in <em>Benutzer</em> hinzufügen und Daten unter <em>Stammdaten → Daten-Übertragung</em> einspielen.
              </p>
            </form>
          ) : (
          <form onSubmit={forgotMode ? onForgot : onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">E-Mail-Adresse</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1.5 h-11 focus-visible:ring-slate-900"
                data-testid="login-email"
              />
            </div>
            {!forgotMode && (
              <div>
                <Label htmlFor="password" className="text-slate-700">Passwort</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="mt-1.5 h-11 focus-visible:ring-slate-900"
                  data-testid="login-password"
                />
              </div>
            )}
            {err && (
              <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}
            {forgotMsg && (
              <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-sm">
                {forgotMsg}
              </div>
            )}
            <Button
              type="submit" disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 text-base"
              data-testid={forgotMode ? "forgot-submit" : "login-submit"}
            >
              {loading ? "..." : (forgotMode ? "Reset-Link senden" : "Anmelden")}
            </Button>
            <div className="flex justify-between text-xs pt-2">
              <button
                type="button"
                className="text-slate-600 hover:text-slate-900 underline"
                onClick={() => { setForgotMode(!forgotMode); setErr(null); setForgotMsg(null); }}
                data-testid="toggle-forgot"
              >
                {forgotMode ? "← Zurück zur Anmeldung" : "Passwort vergessen?"}
              </button>
              <span className="text-slate-400">v1.0</span>
            </div>
          </form>
          )}

          {!setupOpen && (
            <div className="mt-8 pt-6 border-t border-slate-200 text-[11px] text-slate-500 text-center" data-testid="login-info-hint">
              Sie werden nach der Anmeldung automatisch in den passenden Bereich (Verwaltung, Eigentümer- oder Mieter-Portal) weitergeleitet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label }) {
  return (
    <div className="bg-slate-800 rounded-sm p-3 flex flex-col gap-1 border border-slate-700">
      <div className="text-slate-300">{icon}</div>
      <div className="text-[11px] text-slate-300 uppercase tracking-wider">{label}</div>
    </div>
  );
}
